import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Send as SendIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../services/api';

const DigestSettingsModal = ({ open, onClose, onSettingsSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [settings, setSettings] = useState({
    isEnabled: false,
    recipientEmails: [],
    scheduleType: 'manual',
    scheduleDay: '',
    lastSentAt: null,
  });
  
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await csAdminApi.getDigestSettings();
      const data = res.data?.data || {};
      
      setSettings({
        isEnabled: data.is_enabled === 1,
        recipientEmails: Array.isArray(data.recipient_emails) 
          ? data.recipient_emails 
          : (data.recipient_emails ? JSON.parse(data.recipient_emails) : []),
        scheduleType: data.schedule_type || 'manual',
        scheduleDay: data.schedule_day || '',
        lastSentAt: data.last_sent_at,
      });
    } catch (err) {
      console.error('Error fetching digest settings:', err);
      enqueueSnackbar('Error loading settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    const email = newEmail.trim();
    
    // Validate email
    if (!email) {
      setEmailError('Email is required');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Invalid email format');
      return;
    }
    
    if (settings.recipientEmails.includes(email)) {
      setEmailError('Email already added');
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, email],
    }));
    setNewEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setSettings(prev => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter(email => email !== emailToRemove),
    }));
  };

  const handleSave = async () => {
    if (settings.isEnabled && settings.recipientEmails.length === 0) {
      enqueueSnackbar('Please add at least one recipient email', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      await csAdminApi.updateDigestSettings({
        is_enabled: settings.isEnabled ? 1 : 0,
        recipient_emails: JSON.stringify(settings.recipientEmails),
        schedule_type: settings.scheduleType,
        schedule_day: settings.scheduleType === 'weekly' ? settings.scheduleDay : 
                      settings.scheduleType === 'monthly' ? settings.scheduleDay : null,
      });
      
      enqueueSnackbar('Digest settings saved successfully', { variant: 'success' });
      onSettingsSaved?.();
      onClose();
    } catch (err) {
      console.error('Error saving settings:', err);
      enqueueSnackbar('Error saving settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (settings.recipientEmails.length === 0) {
      enqueueSnackbar('Please add at least one recipient email', { variant: 'warning' });
      return;
    }
    try {
      setSending(true);
      await csAdminApi.sendDigestNow({
        recipient_emails: settings.recipientEmails,
      });
      
      enqueueSnackbar('Digest email sent successfully', { variant: 'success' });
      fetchSettings(); // Refresh to get updated last_sent_at
    } catch (err) {
      console.error('Error sending digest:', err);
      enqueueSnackbar('Error sending digest email', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (settings.recipientEmails.length === 0) {
      enqueueSnackbar('Please add at least one recipient email', { variant: 'warning' });
      return;
    }
    try {
      setSending(true);
      await csAdminApi.sendTestDigest({
        recipient_emails: settings.recipientEmails,
      });
      
      enqueueSnackbar('Test email sent successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error sending test email:', err);
      enqueueSnackbar('Error sending test email', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const formatLastSent = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Activity Digest Settings</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Enable/Disable */}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.isEnabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, isEnabled: e.target.checked }))}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Enable Email Notifications
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Receive activity digest emails
                  </Typography>
                </Box>
              }
            />

            <Divider />

            {/* Schedule Type */}
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Schedule
              </Typography>
              <RadioGroup
                value={settings.scheduleType}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  scheduleType: e.target.value,
                  scheduleDay: e.target.value === 'weekly' ? 'monday' : 
                              e.target.value === 'monthly' ? '1' : '',
                }))}
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Manual Only</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Send digest on demand using "Send Now" button
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="weekly"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Weekly</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Every Monday at 8:00 AM
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="monthly"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Monthly</Typography>
                      <Typography variant="caption" color="textSecondary">
                        1st of every month at 8:00 AM
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>

            <Divider />

            {/* Recipients */}
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Recipients
              </Typography>
              
              {/* Add Email Input */}
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddEmail();
                    }
                  }}
                  error={!!emailError}
                  helperText={emailError}
                  InputProps={{
                    startAdornment: <EmailIcon fontSize="small" color="action" sx={{ mr: 1 }} />,
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddEmail}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>

              {/* Email List */}
              <Box display="flex" flexWrap="wrap" gap={1}>
                {settings.recipientEmails.length === 0 ? (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    No recipients added yet. Add email addresses to receive digests.
                  </Alert>
                ) : (
                  settings.recipientEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveEmail(email)}
                      color="primary"
                      variant="outlined"
                    />
                  ))
                )}
              </Box>
            </Box>

            {/* Last Sent */}
            {settings.lastSentAt && (
              <Box>
                <Typography variant="caption" color="textSecondary">
                  <SuccessIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Last sent: {formatLastSent(settings.lastSentAt)}
                </Typography>
              </Box>
            )}

            {/* Test/Send Buttons */}
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={handleSendTest}
                disabled={sending || settings.recipientEmails.length === 0}
                size="small"
              >
                Send Test Email
              </Button>
              <Button
                variant="outlined"
                onClick={handleSendNow}
                disabled={sending || settings.recipientEmails.length === 0}
                startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                size="small"
              >
                Send Now
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DigestSettingsModal;
